/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import React from 'react';
import classNames from 'classnames';

import {ControlContext} from "../util/controlContext";
import {convertColor} from "../util/colorUtil";
import {calculateTextWidth, getComputedProperty, remToPx} from "../util/controlUtil";

export enum LabelType {
    LABEL, SPAN, PAR,
}

export enum BreakType {
    CHAR, WORD
}

interface Props {
    labelType: LabelType,
    className: string,
    text: string,
    color?: string,
    maxLines: number,
    width?: string | number,
    labelProps: object,
    breakMode: BreakType,
}

interface State {
    text: string,
}

/**
 * Label component which breaks its contents automatically. If maxLines is specified, the label will only break the
 * text until the number of lines is reached. Remaining text will be shown in the title.
 */
export default class Label extends React.Component<Props, State> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    static defaultProps = {
        labelType: LabelType.LABEL,
        className: '',
        labelProps: {},
        breakMode: BreakType.CHAR,
        maxLines: 0,
    };

    label: HTMLElement | null = null;

    state = {
        text: '',
    };

    getColor = (): string => {
        const {contrastColor} = this.context, {color} = this.props, finalColor: string = color ? color : contrastColor;
        return convertColor(finalColor);
    };

    getWidth = (): string => {
        const {width} = this.props;
        let finalWidth: string = 'initial';
        if (width !== undefined && width !== null) {
            if (typeof width === 'string') finalWidth = width;
            else finalWidth = width + 'px';
        }
        return finalWidth;
    };

    getLabelProps = () => {
        const {className, labelProps, text} = this.props;
        return {
            className: classNames('dvr-label', className),
            style: {
                color: this.getColor(),
                width: this.getWidth(),
                display: 'inline-block',
            },
            title: text,
            ref: (ref: HTMLElement | null) => {
                this.label = ref
            },
            ...labelProps,
        };
    };

    getFontSize = (): number | undefined => {
        if (this.label) {
            let fontSize: number | string = getComputedProperty('font-size', this.label);
            fontSize = parseInt(fontSize.slice(0, fontSize.length - 2), 10);
            return fontSize;
        }
        return undefined;
    };

    /**
     * Truncate given text to given maxWidth. Truncating is done by removing characters from the end until the text + ...
     * width is less then the given maxWidth. Trailing white spaces are removed and ... are appended.
     * @param text Text to truncate
     * @param maxWidth Max width in px
     */
    reduceText = (text: string, maxWidth: number): string => {
        let fontSize, fontFamily;
        if (this.label) {
            fontSize = this.getFontSize();
            fontFamily = getComputedProperty('font-family', this.label);
        }
        while (calculateTextWidth(text + '...', fontSize, fontFamily) >= maxWidth)
            text = text.slice(0, -1);
        return text.trimRight() + '...';
    };

    /**
     * Break a line by removing characters from the end until a whitespace or dash is found. Removed characters are
     * returned as remainingChars.
     * @param validLine Text to break
     * @return Object containing the remainder of the the line and the removed characters.
     */
    breakLine = (validLine: string): { validLine: string, remainingChars: string } => {
        let remainingChars = '';
        if (/[ -]$/.test(validLine)) {
            validLine = validLine.slice(0, -1);
            remainingChars = ' ';
        }

        while (!/[ -]$/.test(validLine) && validLine.length) {
            remainingChars = validLine.slice(-1) + remainingChars;
            validLine = validLine.slice(0, -1);
        }
        return {validLine, remainingChars};
    };

    /**
     * Process the given text. This function divides the text into lines, by breaking the text according to the break
     * mode given in the props. The width of the text is calculating by using a non-mounted canvas. The font family and
     * font size are calculated from the computed properties of the rendered label.
     */
    renderText = () => {
        const {text, maxLines, breakMode} = this.props, chars = text.split(''), width = this.getWidth();
        if (width === 'initial') return text;

        const pxWidth: number = width.endsWith('px') ? Number(width.slice(0, -2)) : remToPx(Number(width.slice(0, -3))),
            lines: string[] = new Array<string>();
        let fontSize, fontFamily;
        if (this.label) {
            fontSize = this.getFontSize();
            fontFamily = getComputedProperty('font-family', this.label);
        }

        let line = '';
        for (let i = 0; i < chars.length; i++) {
            const char = chars[i];
            if (calculateTextWidth(line + char, fontSize, fontFamily) < pxWidth)
                line += char;
            else {
                let processedLine = line;
                if (breakMode === BreakType.WORD) {
                    const {validLine, remainingChars} = this.breakLine(line);
                    processedLine = validLine;
                    line = remainingChars;
                } else line = '';

                lines.push(processedLine);
                if (maxLines > 0 && lines.length === maxLines) {
                    if (i < chars.length)
                    // Truncate last line if chars remaining
                        lines[lines.length - 1] = this.reduceText(lines[lines.length - 1], pxWidth);
                    line = '';
                    break;
                }
                line += char;
            }
        }
        if (line.length) lines.push(line);
        this.setState({text: lines.join('\n')});
    };

    componentDidMount = () => {
        // Make sure the label has mounted before calculating the text
        window.setTimeout(this.renderText, 0);
    };

    render = () => {
        const {labelType} = this.props, {text} = this.state, labelProps = this.getLabelProps();
        switch (labelType) {
            case LabelType.LABEL:
                return <label {...labelProps}>{text}</label>;
            case LabelType.PAR:
                return <p {...labelProps}>{text}</p>;
            case LabelType.SPAN:
                return <span {...labelProps}>{text}</span>;
        }
    };
}